import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import { restaurantTableService } from '../../../../services/restaurantTableService';
import {
  CirclePlus, RefreshCw, SquarePen, Trash2, LayoutGrid,
  CheckCircle2, Users, LayoutPanelTop,
} from 'lucide-react';
import '../RestaurantPage.css';

const STATUS_BADGE: Record<string, string> = {
  Available: 'ims-badge-approved',
  Occupied:  'ims-badge-rejected',
  Reserved:  'ims-badge-pending',
};

const TableListPage: React.FC = () => {
  const navigate   = useNavigate();
  const { t }      = useTranslation();
  const [items,    setItems]    = useState<any[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [refreshing,setRefreshing] = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  const unwrap = (res: any): any[] => {
    const d = res?.data?.data;
    return Array.isArray(d) ? d : (d?.data ?? []);
  };

  const loadItems = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems(unwrap(await restaurantTableService.getAll())); }
    catch { setError(t('table.failedToLoad', 'Failed to load tables.')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { setItems(unwrap(await restaurantTableService.getAll())); }
    catch { setError(t('table.failedToLoad', 'Failed to load tables.')); }
    finally { setRefreshing(false); }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm(t('table.deleteConfirm'))) return;
    try {
      await restaurantTableService.delete(row.id);
      setItems((prev) => prev.filter((i) => i.id !== row.id));
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? t('common.error'));
    }
  };

  const columns: GridColumn[] = [
    { key: 'tableNumber', label: t('table.tableNumber'), sortable: true, width: '100px',
      render: (v) => <strong>{v}</strong> },
    { key: 'name',        label: t('table.tableName'),   sortable: true, width: '160px' },
    { key: 'capacity',    label: t('table.capacity'),    sortable: true, width: '90px' },
    { key: 'section',     label: t('table.section'),     sortable: true, width: '130px',
      render: (v) => <span>{v || '—'}</span> },
    { key: 'shape',       label: t('table.shape'),       width: '110px' },
    { key: 'liveStatus',  label: 'Status',               width: '120px',
      render: (v) => <span className={`ims-badge ${STATUS_BADGE[v] ?? 'ims-badge-draft'}`}>{t(`table.liveStatus.${v}`, v)}</span> },
    { key: 'currentOrderNumber', label: t('table.currentOrder', 'Order'), width: '120px',
      render: (v) => <span>{v ?? '—'}</span> },
    { key: 'isActive',    label: t('common.status'),     width: '100px',
      render: (v) => <span className={`ims-badge ${v ? 'ims-badge-approved' : 'ims-badge-closed'}`}>{v ? t('common.active') : t('common.inactive')}</span> },
  ];

  const actions: GridAction[] = [
    { icon: <SquarePen size={16} />, onClick: (r) => navigate(`/restaurant/tables/${r.id}/edit`),
      className: 'grid-action-btn-edit', title: t('common.edit') },
    { icon: <Trash2 size={16} />,    onClick: handleDelete,
      className: 'grid-action-btn-delete', title: t('common.delete') },
  ];

  const available = items.filter((i) => i.liveStatus === 'Available').length;
  const occupied  = items.filter((i) => i.liveStatus === 'Occupied').length;

  return (
    <Layout>
      <div className="ims-page rest-page">
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('table.title')}</h1>
            <p className="ims-list-subtitle">{t('table.floorPlanDesc', 'Manage your restaurant tables')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={15} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/restaurant/floor-plan')}
              style={{ background: 'var(--ims-raw-indigo-600)', borderColor: 'var(--ims-raw-indigo-600)' }}>
              <LayoutGrid size={16} /> {t('table.floorPlan')}
            </button>
            <button className="btn-new" onClick={() => navigate('/restaurant/tables/create')}>
              <CirclePlus size={16} /> {t('table.newTable')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-orange"><LayoutPanelTop size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">Total Tables</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon rest-icon-green"><CheckCircle2 size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{available}</span>
              <span className="ims-stat-label">Available</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon" style={{ background: 'var(--ims-red-bg)', color: 'var(--ims-red-text)' }}>
              <Users size={20} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{occupied}</span>
              <span className="ims-stat-label">Occupied</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error">
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-grid-wrapper">
          <DataGrid data={items} columns={columns} actions={actions} loading={loading}
            pageSize={10} searchPlaceholder={t('common.search')}
            emptyMessage={t('table.noTablesFound', 'No tables found. Create your first table.')}
            rowsPerPageOptions={[5, 10, 25, 50]} />
        </div>
      </div>
    </Layout>
  );
};

export default TableListPage;
